import React, { useState, useEffect } from 'react';
import '../../css/userForm.css';
import apiService from '../../shared/services/ApiService';

const UserForm = ({ onSubmit, user, onCancel }) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        email: '',
        role: '',
        brigadas: []
    });
    const [roles, setRoles] = useState([]);
    const [brigadas, setBrigadas] = useState([]);
    const [loading] = useState(false); // eslint-disable-line no-unused-vars
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    useEffect(() => {
        if (user) {
            // Normalizar brigadas: aceptar array o singular (backward compat)
            let userBrigadas = [];
            if (user.brigadas && Array.isArray(user.brigadas)) {
                userBrigadas = user.brigadas.map(b => b._id || b);
            } else if (user.brigada) {
                userBrigadas = [user.brigada._id || user.brigada];
            }
            setFormData({
                username: user.username || '',
                password: '',
                email: user.email || '',
                role: user.role?._id || user.role || '',
                brigadas: userBrigadas
            });
        } else {
            setFormData({
                username: '',
                password: '',
                email: '',
                role: '',
                brigadas: []
            });
        }

        fetchRoles();
        fetchBrigadas();
    }, [user]);

    const fetchRoles = async () => {
        try {
            const response = await apiService.get(`/api/roles`);
            setRoles(response.data);
        } catch (error) {
            console.error('Error al cargar roles:', error);
        }
    };

    const fetchBrigadas = async () => {
        try {
            const response = await apiService.get(`/api/brigadas`);
            setBrigadas(response.data);
        } catch (error) {
            console.error('Error al cargar brigadas:', error);
        }
    };

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 6) strength++;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        return strength;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Calcular fuerza de contraseña
        if (name === 'password') {
            setPasswordStrength(calculatePasswordStrength(value));
        }

        // Limpiar error
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleBrigadaToggle = (brigadaId) => {
        setFormData(prev => {
            const current = prev.brigadas || [];
            const exists = current.includes(brigadaId);
            return {
                ...prev,
                brigadas: exists
                    ? current.filter(id => id !== brigadaId)
                    : [...current, brigadaId]
            };
        });
        if (errors.brigadas) {
            setErrors(prev => ({ ...prev, brigadas: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.username.trim()) {
            newErrors.username = 'El nombre de usuario es requerido';
        } else if (formData.username.trim().length < 3) {
            newErrors.username = 'El nombre debe tener al menos 3 caracteres';
        }
        
        // Solo validar contraseña si es usuario nuevo o si se está modificando
        if (!user && !formData.password) {
            newErrors.password = 'La contraseña es requerida';
        } else if (formData.password && formData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }
        
        if (!formData.email) {
            newErrors.email = 'El correo electrónico es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'El correo electrónico no es válido';
        }
        
        if (!formData.role) {
            newErrors.role = 'Debe seleccionar un rol';
        }
        
        if (!formData.brigadas || formData.brigadas.length === 0) {
            newErrors.brigadas = 'Debe seleccionar al menos una brigada';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Si estamos editando y la contraseña está vacía, no enviarla
        const submitData = { ...formData };
        if (user && !submitData.password) {
            delete submitData.password;
        }
        
        onSubmit(submitData);
    };

    const getPasswordStrengthLabel = () => {
        if (passwordStrength === 0) return '';
        if (passwordStrength <= 2) return 'Débil';
        if (passwordStrength <= 3) return 'Media';
        return 'Fuerte';
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 2) return '#e74c3c';
        if (passwordStrength <= 3) return '#f39c12';
        return '#2ecc71';
    };

    if (loading && !roles.length && !brigadas.length) {
        return (
            <div className="form-loading-modern">
                <div className="spinner-modern"></div>
                <p>Cargando datos del formulario...</p>
            </div>
        );
    }

    return (
        <div className="user-form-container-modern">
            <div className="form-header-modern">
                <div className="form-icon-modern">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                    </svg>
                </div>
                <h3 className="form-title-modern">
                    {user ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </h3>
            </div>
            
            <form onSubmit={handleSubmit} className="user-form-modern">
                <div className="form-content-modern">
                    {/* Nombre de usuario */}
                    <div className={`form-group-modern ${errors.username ? 'has-error' : ''}`}>
                        <label htmlFor="username" className="form-label-modern">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                            Nombre de Usuario
                            <span className="required-modern">*</span>
                        </label>
                        <input
                            id="username"
                            name="username"
                            type="text"
                            placeholder="Ej: juan.perez"
                            value={formData.username}
                            onChange={handleChange}
                            className={`form-input-modern ${errors.username ? 'input-error' : ''}`}
                            maxLength={30}
                        />
                        {errors.username && (
                            <span className="error-message-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {errors.username}
                            </span>
                        )}
                        <div className="input-hint-modern">
                            {formData.username.length}/30 caracteres
                        </div>
                    </div>
                    
                    {/* Contraseña */}
                    <div className={`form-group-modern ${errors.password ? 'has-error' : ''}`}>
                        <label htmlFor="password" className="form-label-modern">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            Contraseña
                            {!user && <span className="required-modern">*</span>}
                            {user && <span className="optional-modern">(Opcional - solo si desea cambiarla)</span>}
                        </label>
                        <div className="password-input-wrapper">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder={user ? "Dejar vacío para mantener actual" : "Mínimo 6 caracteres"}
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-input-modern ${errors.password ? 'input-error' : ''}`}
                            />
                            <button
                                type="button"
                                className="toggle-password-btn"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                        <line x1="1" y1="1" x2="23" y2="23"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                        <circle cx="12" cy="12" r="3"/>
                                    </svg>
                                )}
                            </button>
                        </div>
                        {formData.password && (
                            <div className="password-strength">
                                <div className="strength-bar">
                                    <div 
                                        className="strength-fill"
                                        style={{
                                            width: `${(passwordStrength / 5) * 100}%`,
                                            backgroundColor: getPasswordStrengthColor()
                                        }}
                                    />
                                </div>
                                <span className="strength-label" style={{ color: getPasswordStrengthColor() }}>
                                    {getPasswordStrengthLabel()}
                                </span>
                            </div>
                        )}
                        {errors.password && (
                            <span className="error-message-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {errors.password}
                            </span>
                        )}
                    </div>
                    
                    {/* Email */}
                    <div className={`form-group-modern ${errors.email ? 'has-error' : ''}`}>
                        <label htmlFor="email" className="form-label-modern">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            Correo Electrónico
                            <span className="required-modern">*</span>
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="ejemplo@correo.com"
                            value={formData.email}
                            onChange={handleChange}
                            className={`form-input-modern ${errors.email ? 'input-error' : ''}`}
                        />
                        {errors.email && (
                            <span className="error-message-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {errors.email}
                            </span>
                        )}
                    </div>
                    
                    {/* Rol y Brigada */}
                    <div className="form-row-modern">
                        <div className={`form-group-modern ${errors.role ? 'has-error' : ''}`}>
                            <label htmlFor="role" className="form-label-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                Rol
                                <span className="required-modern">*</span>
                            </label>
                            <select
                                id="role"
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                className={`form-select-modern ${errors.role ? 'input-error' : ''}`}
                            >
                                <option value="">Seleccionar Rol</option>
                                {roles.map((role) => (
                                    <option key={role._id} value={role._id}>
                                        {role.name}
                                    </option>
                                ))}
                            </select>
                            {errors.role && (
                                <span className="error-message-modern">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="12"/>
                                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                    {errors.role}
                                </span>
                            )}
                        </div>
                        
                        <div className={`form-group-modern ${errors.brigadas ? 'has-error' : ''}`}>
                            <label className="form-label-modern">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                                Brigadas
                                <span className="required-modern">*</span>
                            </label>
                            <div className={`brigada-checkbox-list ${errors.brigadas ? 'input-error' : ''}`}>
                                {brigadas.map((brigada) => (
                                    <label key={brigada._id} className="brigada-checkbox-item">
                                        <input
                                            type="checkbox"
                                            checked={(formData.brigadas || []).includes(brigada._id)}
                                            onChange={() => handleBrigadaToggle(brigada._id)}
                                        />
                                        <span className="brigada-checkbox-label">{brigada.name}</span>
                                    </label>
                                ))}
                                {brigadas.length === 0 && (
                                    <span className="no-brigadas-text">No hay brigadas disponibles</span>
                                )}
                            </div>
                            {formData.brigadas && formData.brigadas.length > 0 && (
                                <div className="selected-brigadas-count">
                                    {formData.brigadas.length} brigada{formData.brigadas.length !== 1 ? 's' : ''} seleccionada{formData.brigadas.length !== 1 ? 's' : ''}
                                </div>
                            )}
                            {errors.brigadas && (
                                <span className="error-message-modern">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="12"/>
                                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                    {errors.brigadas}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className="form-actions-modern">
                    <button 
                        type="button" 
                        onClick={onCancel} 
                        className="cancel-button-modern"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                        Cancelar
                    </button>
                    <button 
                        type="submit" 
                        className="submit-button-modern"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                            <polyline points="17 21 17 13 7 13 7 21"/>
                            <polyline points="7 3 7 8 15 8"/>
                        </svg>
                        {user ? 'Actualizar Usuario' : 'Crear Usuario'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserForm;